import React, { PureComponent } from 'react';
import { bool, string, shape, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { reducerAccumulator } from '@widergy/web-utils/lib/array';
import { newCurrentAccount } from '@widergy/web-utils/lib/account';
import { getURLParams } from '@widergy/web-utils/lib/browser';

import { accountPropTypes } from 'redux/accountHandlers';
import { actionCreators as paymentActions } from 'redux/paymentHandlers';
import { accountUnpaidType } from 'types/paymentTypes';
import { equals } from 'utils/objectUtils';
import { ROUTES } from 'constants/routes';
import WithoutAccounts from 'app/components/WithoutAccounts';
import { appConfig } from 'config/customization';
import { gaTrackPageView } from 'services/AnalyticsService';

import { NIS } from '../../../PublicDashboard/screens/Payments/constants';

import PaymentsScene from './layout';
import styles from './styles';

const publicHome = appConfig.publicDashboard.home ? ROUTES.PUBLIC_HOME : ROUTES.PUBLIC_PAYMENTS;

const paymentDetailsMapper = (clientNumber, bills) =>
  bills.map(bill => ({
    client_number: clientNumber,
    external_id: bill.external_id || bill.value.external_id,
    amount: bill.balance || bill.amount || bill.value.balance || bill.value.amount,
    barcode: bill.barcode || (bill.value && bill.value.barcode)
  }));

class PaymentsSceneContainer extends PureComponent {
  state = { totalAmount: 0, amountList: [], paymentDocumentModalIsOpen: false, paymentDocumentAccount: null };

  componentDidMount() {
    const { route, currentAccount, payableDocuments, payableDocumentsLoading, accountUnpaid } = this.props;
    gaTrackPageView(route);
    if (route === ROUTES.PAYMENT_GATEWAY) this.handlePaymentGateway();
    else if (route === ROUTES.PAYMENTS) {
      if (currentAccount) this.handleRetrieveAccountDebt(currentAccount);
      else this.goToHome();
    } else if (route === ROUTES.EXTERNAL_DOCUMENT_PAYMENTS || route === ROUTES.PUBLIC_PAYMENT_DOCUMENTS) {
      if (!payableDocuments && !payableDocumentsLoading && !accountUnpaid) {
        this.goToHome();
      } else if (payableDocuments || accountUnpaid)
        this.handleChangeCheckedAccount(payableDocuments ? payableDocuments[0] : accountUnpaid[0]);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      route,
      currentAccount,
      payableDocuments,
      payableDocumentsLoading,
      accountUnpaid,
      accountUnpaidLoading,
      paymentRequest
    } = this.props;
    if (route === ROUTES.PAYMENTS && newCurrentAccount(prevProps, this.props))
      this.handleRetrieveAccountDebt(currentAccount);
    else if (route === ROUTES.EXTERNAL_DOCUMENT_PAYMENTS || route === ROUTES.PUBLIC_PAYMENT_DOCUMENTS) {
      if (payableDocumentsLoading !== prevProps.payableDocumentsLoading) {
        if (payableDocuments || accountUnpaid)
          this.handleChangeCheckedAccount(payableDocuments ? payableDocuments[0] : accountUnpaid[0]);
        else this.goToHome();
      } else if (!payableDocuments && !accountUnpaid) this.goToHome();
    } else if (paymentRequest && !equals(prevProps.paymentRequest, paymentRequest)) this.goToHome();
    else if (route === ROUTES.PAYMENTS && !accountUnpaidLoading && !accountUnpaid) this.goToHome();
  }

  goToHome = () => this.props.dispatch(push(this.props.isPublic ? publicHome : ROUTES.ACCOUNT_STATUS));

  handlePaymentGateway = () => {
    const params = getURLParams(this.props.location.search, NIS);
    if (params[NIS]) {
      this.props.dispatch(paymentActions.getPayableDocuments(params[NIS]));
      this.props.dispatch(push(ROUTES.EXTERNAL_DOCUMENT_PAYMENTS));
    } else this.props.dispatch(push(ROUTES.ACCOUNT_STATUS));
  };

  handleRetrieveAccountDebt = account => {
    this.props.dispatch(paymentActions.getAccountUnpaid(account));
    this.setState({ openClientNumber: account.client_number, checkedAccount: account.client_number });
  };

  componentWillUnmount = () => this.props.dispatch(paymentActions.resetPayableDocuments());

  setTotalAmount = (clientNumber, bills) => {
    const paymentBills = bills && paymentDetailsMapper(clientNumber, bills);
    const amountList = paymentBills || [];
    const totalAmount = amountList.map(item => item.amount).reduce(reducerAccumulator, 0);
    this.setState({ amountList, totalAmount });
  };

  handleChangeCheckedAccount = account => this.setState({ checkedAccount: account.client_number });

  handleOnPress = clientNumber => () =>
    this.setState(prevState => ({
      openClientNumber: prevState.openClientNumber !== clientNumber && clientNumber
    }));

  handlePayment = () => {
    const { totalAmount, amountList } = this.state;
    this.props.dispatch(paymentActions.paymentFlowInit(totalAmount, amountList));
  };

  handleExternalAccPayment = () => this.props.dispatch(push(ROUTES.EXTERNAL_PAYMENTS));

  handleOpenPaymentDocumentModal = account =>
    this.setState({ paymentDocumentModalIsOpen: true, paymentDocumentAccount: account });

  handleClosePaymentDocumentModal = () => this.setState({ paymentDocumentModalIsOpen: false });

  handleDownloadPaymentDocument = async () => {
    await this.props.dispatch(
      appConfig.payments.paymentDocumentDownloadOnPDF
        ? paymentActions.getPaymentDocumentPDF(this.state.paymentDocumentAccount.client_number)
        : paymentActions.getPaymentDocument(this.state.paymentDocumentAccount.client_number)
    );
    this.setState({ paymentDocumentModalIsOpen: false, paymentDocumentAccount: null });
  };

  render() {
    const accountsWithUnpaid = this.props.payableDocuments || this.props.accountUnpaid;

    return (
      <div style={styles.container}>
        <PaymentsScene
          loading={this.props.payableDocumentsLoading || this.props.accountUnpaidLoading}
          accountsWithUnpaid={accountsWithUnpaid}
          handlePayment={this.handlePayment}
          handleExternalAccPayment={this.handleExternalAccPayment}
          handleOnPress={this.handleOnPress}
          openClientNumber={this.state.openClientNumber}
          handleChangeCheckedAccount={this.handleChangeCheckedAccount}
          checkedAccount={this.state.checkedAccount}
          disablePaymentButton={this.state.totalAmount === 0}
          setTotalAmount={this.setTotalAmount}
          totalAmount={this.state.totalAmount}
          isPublic={this.props.isPublic}
          onOpenPaymentDocumentModal={this.handleOpenPaymentDocumentModal}
          onClosePaymentDocumentModal={this.handleClosePaymentDocumentModal}
          onDownloadPaymentDocument={this.handleDownloadPaymentDocument}
          paymentDocumentModalIsOpen={this.state.paymentDocumentModalIsOpen}
        />
      </div>
    );
  }
}

const mapStateToProps = store => ({
  currentAccount: store.account.currentAccount,
  route: store.router.location && store.router.location.pathname,
  accountUnpaid: store.payment.accountUnpaid,
  accountUnpaidLoading: store.payment.accountUnpaidLoading,
  accounts: store.account.accounts,
  payableDocuments: store.payment.payableDocuments,
  payableDocumentsLoading: store.payment.payableDocumentsLoading,
  paymentRequest: store.payment.paymentRequest,
  accountsError: store.account.accountsError
});

PaymentsSceneContainer.propTypes = {
  currentAccount: accountPropTypes,
  route: string,
  accountUnpaid: arrayOf(accountUnpaidType),
  accountUnpaidLoading: bool,
  payableDocuments: arrayOf(accountUnpaidType),
  payableDocumentsLoading: bool,
  isPublic: bool,
  paymentRequest: bool,
  location: shape({
    pathname: string.isRequired,
    search: string
  })
};

export default connect(mapStateToProps)(
  WithoutAccounts({
    shouldShowMessage: props => !props.isPublic && props.accounts.length === 0 && !props.accountsLoading,
    accountsError: props => props.accountsError
  })(PaymentsSceneContainer)
);
